/**
 * Cached state of the ConfigCat `enableAddinsLinksToFactsetWorkstation` flag.
 *
 * Outbound add-in links into FactSet Workstation (e.g. Manage Skills) stay
 * off until this flag resolves to true. Unresolved, missing-client, and reset
 * states read as disabled so a link cannot appear before ConfigCat answers,
 * or after logout.
 *
 * The ConfigCat client lives in the PowerPoint workspace, which `excel/` and
 * `shared/` may not import (see `tools/import-boundary-check`). The service
 * pushes the resolved value here; Excel and PowerPoint UI read it back out.
 */

type AddinsLinksListener = () => void;

let addinsLinksToFactsetWorkstationFlag: boolean | null = false;
const listeners = new Set<AddinsLinksListener>();

function notifyAddinsLinksListeners(): void {
  listeners.forEach((listener) => listener());
}

export function setAddinsLinksToFactsetWorkstationFlag(value: boolean): void {
  if (addinsLinksToFactsetWorkstationFlag === value) return;
  addinsLinksToFactsetWorkstationFlag = value;
  notifyAddinsLinksListeners();
}

export function resetAddinsLinksToFactsetWorkstationFlag(): void {
  const changed = addinsLinksToFactsetWorkstationFlag !== false;
  addinsLinksToFactsetWorkstationFlag = false;
  if (changed) notifyAddinsLinksListeners();
}

export function invalidateAddinsLinksToFactsetWorkstationFlag(): void {
  const changed = addinsLinksToFactsetWorkstationFlag !== null;
  addinsLinksToFactsetWorkstationFlag = null;
  if (changed) notifyAddinsLinksListeners();
}

export function isAddinsLinksToFactsetWorkstationEnabled(): boolean {
  return addinsLinksToFactsetWorkstationFlag === true;
}

export function subscribeAddinsLinksToFactsetWorkstation(
  listener: AddinsLinksListener,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
